/**
 * Répartir les enfants d'une session en sous-groupes.
 *
 * ## Ce qu'on fait sur le terrain
 *
 * 48 enfants chez les Hirondelles : les 24 plus jeunes forment les « Petites
 * Hirondelles », les 24 plus âgés les « Grandes ». 32 Colibris : quatre
 * groupes de 8, « Jaunes », « Bleus », « Verts », « Rouges », pour que les
 * enfants retiennent facilement le leur. La répartition se fait donc par âge
 * et en parts égales, et ce sont les équipes qui nomment les groupes.
 *
 * ## Par date de naissance, pas par âge en années
 *
 * Deux enfants de « 5 ans » peuvent avoir presque un an d'écart : la date de
 * naissance départage là où l'âge entier ne le ferait pas. Les plus jeunes
 * vont dans le premier groupe, dans l'ordre où les noms sont donnés. Un enfant
 * sans date de naissance va à la fin, et l'aperçu le signale.
 *
 * ## Parts égales à un enfant près
 *
 * 49 enfants en 2 groupes font 25 et 24 : le surplus va aux premiers groupes,
 * un enfant chacun. L'ancien calcul (`auto_divide_by_age`, côté base) pouvait
 * laisser le dernier groupe nettement plus petit : 10 enfants en 4 groupes y
 * donnaient 3, 3, 3 et 1.
 */

export interface EnfantARepartir {
  inscriptionId: string;
  nom: string;
  prenom: string;
  /** AAAA-MM-JJ, ou null si la fiche n'en a pas. */
  dateNaissance: string | null;
}

export interface GroupePlanifie {
  nom: string;
  enfants: EnfantARepartir[];
  /** Âge à la date de référence, en années ; null si aucun enfant daté. */
  ageMin: number | null;
  ageMax: number | null;
}

/** Les noms que les équipes reprennent d'une semaine à l'autre. */
export const MODELES_DE_NOMS: { libelle: string; noms: string[] }[] = [
  { libelle: 'Petits / Grands', noms: ['Petits', 'Grands'] },
  { libelle: 'Jaunes / Bleus / Verts / Rouges', noms: ['Jaunes', 'Bleus', 'Verts', 'Rouges'] },
];

export function nomsParDefaut(nombre: number): string[] {
  return Array.from({ length: nombre }, (_, i) => `Groupe ${i + 1}`);
}

/** Un nom de groupe tel qu'on l'enregistre : sans espaces superflus. */
export function normaliserNom(nom: string): string {
  return nom.replace(/\s+/g, ' ').trim();
}

const cleDeNom = (nom: string) => normaliserNom(nom).toLocaleLowerCase('fr');

/**
 * Ce qui empêche d'utiliser ces noms, ou null s'ils conviennent.
 * Deux groupes du même nom seraient indiscernables sur les listes imprimées.
 */
export function erreurNoms(noms: string[]): string | null {
  if (noms.length === 0) return 'Indiquez au moins un groupe.';
  if (noms.some((n) => normaliserNom(n) === '')) return 'Chaque groupe doit avoir un nom.';
  const vus = new Set<string>();
  for (const n of noms) {
    const cle = cleDeNom(n);
    if (vus.has(cle)) return `Le nom « ${normaliserNom(n)} » est utilisé deux fois.`;
    vus.add(cle);
  }
  return null;
}

/** L'âge en années révolues à une date donnée (AAAA-MM-JJ), ou null. */
export function ageAu(dateNaissance: string | null, date: string): number | null {
  if (!dateNaissance) return null;
  const [an, mois, jour] = dateNaissance.slice(0, 10).split('-').map(Number);
  const [anRef, moisRef, jourRef] = date.slice(0, 10).split('-').map(Number);
  if ([an, mois, jour, anRef, moisRef, jourRef].some((v) => !Number.isFinite(v))) return null;
  let age = anRef - an;
  if (moisRef < mois || (moisRef === mois && jourRef < jour)) age--;
  return age;
}

/** Les plus jeunes d'abord ; sans date à la fin ; à date égale, par nom. */
function comparerParAge(a: EnfantARepartir, b: EnfantARepartir): number {
  if (a.dateNaissance && b.dateNaissance && a.dateNaissance !== b.dateNaissance) {
    return a.dateNaissance < b.dateNaissance ? 1 : -1;
  }
  if (!a.dateNaissance !== !b.dateNaissance) return a.dateNaissance ? -1 : 1;
  return `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr');
}

/**
 * Répartit les enfants en autant de groupes que de noms, par âge et en parts
 * égales à un enfant près.
 *
 * @param dateReference le premier jour de la session, pour les âges affichés
 */
export function repartirParAge(
  enfants: EnfantARepartir[],
  noms: string[],
  dateReference: string,
): GroupePlanifie[] {
  const tries = [...enfants].sort(comparerParAge);
  const base = Math.floor(tries.length / noms.length);
  const surplus = tries.length % noms.length;

  let debut = 0;
  return noms.map((nom, i) => {
    const taille = base + (i < surplus ? 1 : 0);
    const membres = tries.slice(debut, debut + taille);
    debut += taille;
    const ages = membres
      .map((e) => ageAu(e.dateNaissance, dateReference))
      .filter((a): a is number => a !== null);
    return {
      nom: normaliserNom(nom),
      enfants: membres,
      ageMin: ages.length ? Math.min(...ages) : null,
      ageMax: ages.length ? Math.max(...ages) : null,
    };
  });
}

export interface GroupeExistant {
  id: string;
  nom: string;
}

export interface Rapprochement {
  /** Pour chaque nom planifié, le groupe existant à réutiliser, ou null s'il faut le créer. */
  reutilises: Map<string, GroupeExistant | null>;
  /** Les groupes existants absents du plan : ils seront vidés, donc supprimés. */
  aSupprimer: GroupeExistant[];
}

/**
 * Rapproche les noms planifiés des groupes déjà créés pour la session.
 *
 * Un groupe existant est réutilisé quand son nom est le même à la casse près :
 * appliquer deux fois la même répartition ne crée pas de doublons, et c'est ce
 * qui permet de relancer « Appliquer » après une coupure.
 */
export function rapprocherGroupes(existants: GroupeExistant[], noms: string[]): Rapprochement {
  const parCle = new Map(existants.map((g) => [cleDeNom(g.nom), g]));
  const reutilises = new Map<string, GroupeExistant | null>();
  const gardes = new Set<string>();
  for (const nom of noms) {
    const existant = parCle.get(cleDeNom(nom)) ?? null;
    reutilises.set(normaliserNom(nom), existant);
    if (existant) gardes.add(existant.id);
  }
  return { reutilises, aSupprimer: existants.filter((g) => !gardes.has(g.id)) };
}
